import { useState, useEffect } from 'react'

// Статус провайдера для индикатора на карточке модели.
//   healthy  → зелёный  (стабильна и доступна)
//   degraded → жёлтый    (задержки / единичные сбои)
//   down     → красный  (не отвечает — сбой на стороне провайдера)
//   unknown  → серый    (нет данных)
export type ProviderHealthStatus = 'healthy' | 'degraded' | 'down' | 'unknown'

export interface ProviderHealthEntry {
  status: ProviderHealthStatus
  latency_ms: number | null
  error: string | null
  checked_at: string | null
  display_name: string
  recent_errors: number
  // Свежие 5xx по конкретным моделям шлюза: { model_id: count }.
  // Используется для адресного красного индикатора на отдельной модели.
  model_errors?: Record<string, number>
}

export type ProviderHealthMap = Record<string, ProviderHealthEntry>

const API_BASE = import.meta.env.VITE_API_URL || ''
// Как часто обновляем снапшот на клиенте. Бэк пингует провайдеров сам
// (раз в ~3 мин), на клиенте достаточно тянуть снапшот раз в минуту.
const REFRESH_MS = 60_000

// Модульный кэш — все карточки на странице используют один снапшот,
// без дублирующих запросов (аналогично cachedBrands в useProviders).
let cached: ProviderHealthMap | null = null
let lastFetch = 0
let inflight: Promise<ProviderHealthMap> | null = null

async function fetchHealth(signal?: AbortSignal): Promise<ProviderHealthMap> {
  const res = await fetch(`${API_BASE}/api/v1/providers/health`, { signal })
  const data = await res.json()
  if (data?.ok && data.providers) return data.providers as ProviderHealthMap
  return {}
}

/**
 * Возвращает map статусов провайдеров по имени адаптера (model.provider).
 * Не бросает ошибок: при сбое запроса просто отдаёт пустую/предыдущую карту,
 * чтобы каталог моделей всегда рендерился.
 */
export function useProviderHealth(): ProviderHealthMap {
  const [health, setHealth] = useState<ProviderHealthMap>(cached || {})

  useEffect(() => {
    const ctrl = new AbortController()
    let timer: ReturnType<typeof setInterval> | null = null

    const load = async () => {
      // Свежий кэш (моложе REFRESH_MS) переиспользуем без запроса.
      if (cached && Date.now() - lastFetch < REFRESH_MS) {
        setHealth(cached)
        return
      }
      try {
        if (!inflight) {
          inflight = fetchHealth(ctrl.signal)
        }
        const map = await inflight
        cached = map
        lastFetch = Date.now()
        setHealth(map)
      } catch {
        // тихо игнорируем — индикаторы просто останутся 'unknown'
      } finally {
        inflight = null
      }
    }

    load()
    // По таймеру просто вызываем load(): к этому моменту возраст кэша уже
    // >= REFRESH_MS, поэтому произойдёт реальный refetch. При множестве
    // карточек дубли схлопывает inflight + проверка возраста кэша.
    timer = setInterval(load, REFRESH_MS)

    return () => {
      ctrl.abort()
      if (timer) clearInterval(timer)
    }
  }, [])

  return health
}
